/**
 * Audio Download Service Factory
 * 
 * Provides a unified API for audio downloads, allowing easy switching
 * between HLS-based download (local TS) and server transmux (MP4).
 * 
 * Usage:
 * - Set USE_SERVER_TRANSMUX to true for server-side MP4 conversion
 * - Set USE_SERVER_TRANSMUX to false for local HLS/TS download
 */

import type { ProductDataMultimedia, ProductTypeMultimedia } from "@/pages/Workshop/types";
import { getOfflineVideo, generateMediaId } from "./storage/offlineVideosStorage";

// ============================================
// FEATURE FLAG - Change this to switch implementations
// ============================================
// 
// true  = Server transmux (downloads MP4 from server)
// false = Local HLS download (downloads TS segments locally)
//
export const USE_SERVER_TRANSMUX = true;

// ============================================
// Lazy imports to avoid loading unused code
// ============================================

// HLS-based implementation (existing)
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getHLSService = () => import("./audioDownloadService");

// Server transmux implementation (new)
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getTransmuxService = () => import("./audioDownloadServiceTransmux");

// ============================================
// Unified Download Function
// ============================================

export interface AudioDownloadOptions {
	courseClassesId: string;
	productData: ProductDataMultimedia;
	audioName: string;
	duration: number;
	vimeoId: number;
	audioStreamUrl?: string; // HLS URL (for non-transmux)
	estimatedSizeMB: number;
	onProgress?: (progress: number, phase?: string) => void;
}

/**
 * Download audio using the configured implementation
 */
export const downloadAudio = async (options: AudioDownloadOptions): Promise<void> => {
	const {
		courseClassesId,
		productData,
		audioName,
		duration,
		vimeoId,
		audioStreamUrl,
		estimatedSizeMB,
		onProgress,
	} = options;

	if (USE_SERVER_TRANSMUX) {
		// Server transmux - downloads MP4
		const { downloadAudioTransmux } = await getTransmuxService();
		return downloadAudioTransmux(
			courseClassesId,
			productData,
			audioName,
			duration,
			vimeoId,
			estimatedSizeMB,
			onProgress
		);
	} else {
		// HLS download - downloads TS segments
		if (!audioStreamUrl) {
			throw new Error("audioStreamUrl is required for HLS download");
		}
		
		const { downloadAudioHLS } = await getHLSService();
		return downloadAudioHLS(
			courseClassesId,
			productData,
			audioName,
			duration,
			audioStreamUrl,
			estimatedSizeMB,
			vimeoId,
			// Wrap progress callback to match signature
			onProgress ? (progress: number): void => { onProgress(progress); } : undefined
		);
	}
};

// ============================================
// Unified Pause Function
// ============================================

/**
 * Pause an active audio download
 */
export const pauseAudioDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<void> => {
	if (USE_SERVER_TRANSMUX) {
		const { pauseAudioDownloadTransmux } = await getTransmuxService();
		return pauseAudioDownloadTransmux(productType, productId, courseClassesId);
	} else {
		const service = await getHLSService();
		return service.pauseAudioDownload(productType, productId, courseClassesId);
	}
};

// ============================================
// Unified Resume Function
// ============================================

/**
 * Resume a paused audio download
 */
export const resumeAudioDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string,
	onProgress?: (progress: number, phase?: string) => void
): Promise<void> => {
	if (USE_SERVER_TRANSMUX) {
		const { resumeAudioDownloadTransmux } = await getTransmuxService();
		return resumeAudioDownloadTransmux(productType, productId, courseClassesId, onProgress);
	} else {
		const service = await getHLSService();
		// HLS resume doesn't have phase callback
		return service.resumeAudioDownload(
			productType,
			productId,
			courseClassesId,
			onProgress ? (progress: number): void => { onProgress(progress); } : undefined
		);
	}
};

// ============================================
// Unified Cancel Function
// ============================================

/**
 * Cancel audio download and cleanup
 */
export const cancelAudioDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<void> => {
	if (USE_SERVER_TRANSMUX) {
		const { cancelAudioDownloadTransmux } = await getTransmuxService();
		return cancelAudioDownloadTransmux(productType, productId, courseClassesId);
	} else {
		const service = await getHLSService();
		return service.cancelAudioDownload(productType, productId, courseClassesId);
	}
};

// ============================================
// Unified Get Local URI Function
// ============================================

/**
 * Get local audio file URI for playback
 * Automatically detects file format (MP4 or TS) based on metadata
 */
export const getLocalAudioUri = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<string | null> => {
	// Use base key format (without _audio suffix) - same as download
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const audio = await getOfflineVideo(audioId);

	// Verify this is actually an audio entry
	if (!audio || audio.mediaType !== "audio") {
		return null;
	}

	// Check if this is a transmux (MP4) or HLS (TS) file
	const isTransmux = audio.localFilePath?.endsWith(".mp4") || 
		audio.downloadUrl?.startsWith("transmux://");

	if (isTransmux) {
		const { getLocalAudioUriTransmux } = await getTransmuxService();
		return getLocalAudioUriTransmux(productType, productId, courseClassesId);
	} else {
		const service = await getHLSService();
		return service.getLocalAudioUri(productType, productId, courseClassesId);
	}
};

// ============================================
// Unified Cache Clear Functions
// ============================================

/**
 * Clear all audio blob caches
 */
export const clearAudioBlobCache = async (): Promise<void> => {
	// Clear both caches
	const [hlsService, transmuxService] = await Promise.all([
		getHLSService(),
		getTransmuxService(),
	]);
	
	hlsService.clearAudioBlobCache();
	transmuxService.clearAudioBlobCacheTransmux();
};

/**
 * Clear specific audio from cache
 */
export const clearAudioBlobFromCache = async (audioId: string): Promise<void> => {
	const [hlsService, transmuxService] = await Promise.all([
		getHLSService(),
		getTransmuxService(),
	]);
	
	hlsService.clearAudioBlobFromCache(audioId);
	transmuxService.clearAudioBlobFromCacheTransmux(audioId);
};

// ============================================
// Helper: Get MIME Type for Audio File
// ============================================

/**
 * Get the appropriate MIME type for a local audio file
 * @param localFilePath - Path to the local audio file
 * @returns MIME type string
 */
export const getAudioMimeType = (localFilePath: string | undefined): string => {
	if (!localFilePath) {
		return "audio/mp4"; // Default
	}
	
	if (localFilePath.endsWith(".mp4") || localFilePath.endsWith(".m4a")) {
		return "audio/mp4";
	} else if (localFilePath.endsWith(".ts")) {
		// Note: TS files may not play correctly in native players
		// Consider using HLS provider for TS files
		return "video/MP2T";
	}
	
	return "audio/mp4"; // Default fallback
};

// ============================================
// Helper: Check Implementation Type
// ============================================

/**
 * Check if an audio file was downloaded using transmux
 */
export const isTransmuxAudioFile = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<boolean> => {
	// Use base key format (without _audio suffix)
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const audio = await getOfflineVideo(audioId);
	
	if (!audio || audio.mediaType !== "audio") {
		return false;
	}
	
	return audio.localFilePath?.endsWith(".mp4") || 
		audio.downloadUrl?.startsWith("transmux://") ||
		false;
};

/**
 * Get current implementation mode
 */
export const getCurrentImplementation = (): "transmux" | "hls" => {
	return USE_SERVER_TRANSMUX ? "transmux" : "hls";
};
